import { Avatar, Box, Button, TextButton } from "@wix/design-system";
import { useTranslation } from "@wix/fe-essentials-standalone";
import { TEXT_STANDARD_PRIMARY_COLOR } from "@wix/picasso-common";
import {
  ArrowLeft,
  ChevronDownSmall,
  ChevronUpSmall,
} from "@wix/wix-ui-icons-common";
import type { FC } from "react";
import { VerticalDivider } from "../Common/VerticalDivider";
import { WixVibeLogoBetaSvg } from "../Common/WixVibeLogoSvg";
import { MainMenu } from "../MainMenu/MainMenu";
import { useHeader } from "./useHeader";
import { useAppContext } from "../Common/useAppContext";
import { HelpDropdown } from "../HelpDropdown/HelpDropdown";
import { useMobile } from "../../../hooks/useMobile";

interface HeaderProps {
  isLoggedIn: boolean;
}

export const Header: FC<HeaderProps> = ({ isLoggedIn }) => {
  const {
    isMenuDropdownOpen,
    setMenuDropdownOpen,
    shouldShowBackButton,
    handleBackButtonClick,
    handleProfileDropdownToggle,
    handleMySitesClick,
    handleSignInClick,
    handleSignUpClick,
    menuDropdownRef,
    profileAvatarTriggerRef,
    isHelpDropdownOpen,
    setIsHelpDropdownOpen,
    handleHelpDropdownToggle,
    helpDropdownRef,
    helpDropdownTriggerRef,
  } = useHeader();
  const { userDetails } = useAppContext();
  const { t } = useTranslation();
  const isMobile = useMobile();

  return (
    <>
      <Box
        direction="horizontal"
        align="space-between"
        padding={isMobile ? "12px 16px" : "12px 24px"}
        position="relative"
      >
        <Box verticalAlign="middle" gap={4}>
          <WixVibeLogoBetaSvg color={TEXT_STANDARD_PRIMARY_COLOR} />
          {shouldShowBackButton && (
            <Box
              direction="vertical"
              gap={2}
              height="30px"
              position="absolute"
              top={isMobile ? "60px" : "70px"}
            >
              <TextButton
                skin="dark"
                size="medium"
                weight="thin"
                prefixIcon={<ArrowLeft />}
                onClick={handleBackButtonClick}
              >
                {t("landing.header.back_button")}
              </TextButton>
            </Box>
          )}
          {!isMobile && (
            <Box height="30px" verticalAlign="middle" gap={4}>
              <VerticalDivider />
              {isLoggedIn && (
                <>
                  <TextButton
                    skin="dark"
                    size="small"
                    weight="normal"
                    as="a"
                    onClick={handleMySitesClick}
                  >
                    {t("landing.header.my_sites")}
                  </TextButton>
                  <VerticalDivider />
                </>
              )}
              <div ref={helpDropdownTriggerRef}>
                <TextButton
                  skin="dark"
                  size="small"
                  weight="normal"
                  onClick={handleHelpDropdownToggle}
                  suffixIcon={
                    isHelpDropdownOpen ? (
                      <ChevronUpSmall />
                    ) : (
                      <ChevronDownSmall />
                    )
                  }
                >
                  {t("landing.header.help")}
                </TextButton>
              </div>
            </Box>
          )}
        </Box>
        <Box verticalAlign="middle" gap={2} height="56px">
          {!isLoggedIn && (
            <>
              <Button
                skin="light"
                size={isMobile ? "small" : "medium"}
                as="a"
                onClick={handleSignInClick}
              >
                {t("landing.header.sign_in")}
              </Button>
              <Button
                size={isMobile ? "small" : "medium"}
                onClick={handleSignUpClick}
              >
                {t("landing.header.get_started")}
              </Button>
            </>
          )}
          {isLoggedIn && (
            <div ref={profileAvatarTriggerRef}>
              <Avatar
                imgProps={{ src: userDetails?.profileImage }}
                size={isMobile ? "size30" : "size48"}
                onClick={handleProfileDropdownToggle}
              />
            </div>
          )}
        </Box>
      </Box>
      {userDetails && (
        <div ref={menuDropdownRef}>
          <MainMenu
            isOpen={isMenuDropdownOpen}
            setIsOpen={setMenuDropdownOpen}
            closeDropdown={() => setMenuDropdownOpen(false)}
          />
        </div>
      )}
      {isHelpDropdownOpen && (
        <div ref={helpDropdownRef}>
          <HelpDropdown
            onClose={() => setIsHelpDropdownOpen(false)}
            isVisible={isHelpDropdownOpen}
          />
        </div>
      )}
    </>
  );
};
